import { components } from "@apiSchema";
import { ID, includesInLC } from "@datassential/platform-ui-lib";
import dayjs from "dayjs";
import { getUnique } from "@helpers";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import { SocialPosts, ISocialPosts } from "@models/SocialPosts";

export interface ISocialSlice {
    yearGrowthPct: number;
    monthGrowthPct: number;
    data: Array<{
        yearLabel: string;
        months: {
            month: number;
            values: number[];
        }[];
    }>;
    sequences: Array<{
        id: ID;
        title: string;
        color: string;
    }>;
}

export interface IAssociation {
    id: ID;
    name: string;
    count: number;
    oneMonthGrowthPct: number;
}

export interface IFoodSocial extends iPermissionFields {
    data: {
        mentions: ISocialSlice;
        engagement: ISocialSlice;
        foodAssociations: IAssociation[];
        placesAssociations: IAssociation[];
        posts: ISocialPosts;
    };
}

export type ApiModel = components["schemas"]["SocialDataViewModel"];

interface IData {
    apiModel: ApiModel;
}

const generateFakeMonthsData = (initialMonth: number, monthsCount: number, sequencesCount: number = 1) => {
    return Array(monthsCount).fill(0).map((_, index) => ({
        month: index + initialMonth,
        values: Array(sequencesCount).fill(0).map(() => {
            return Math.floor(Math.random() * 1000);
        }),
    }));
}

export enum SocialColor {
    Positive = '#06e1b0',
    Neutral = '#e0e1e4',
    Negative = '#d76cf4',
}

export class FoodSocial implements IFoodSocial {
    static readonly defaultData: IFoodSocial['data'] = {
        mentions: {
            yearGrowthPct: 0,
            monthGrowthPct: 0,
            data: [],
            sequences: [
                { id: 1, title: 'Positive', color: SocialColor.Positive },
                { id: 2, title: 'Neutral', color: SocialColor.Neutral },
                { id: 3, title: 'Negative', color: SocialColor.Negative },
            ],
        },
        engagement: {
            yearGrowthPct: 0,
            monthGrowthPct: 0,
            data: [],
            sequences: [
                { id: 4, title: "Engagement", color: '#2D3A4C' },
            ],
        },
        foodAssociations: [],
        placesAssociations: [],
        posts: SocialPosts.defaultData,
    };

    static readonly fakeData: IFoodSocial['data'] = {
        mentions: {
            yearGrowthPct: 10,
            monthGrowthPct: 5,
            data: [
                {
                    yearLabel: dayjs().add(-2, 'year').format('YYYY'),
                    months: generateFakeMonthsData(
                        dayjs().month() + 2,
                        12 - dayjs().month() - 1,
                        3,
                    ),
                },
                {
                    yearLabel: dayjs().add(-1, 'year').format('YYYY'),
                    months: generateFakeMonthsData(
                        1,
                        12,
                        3,
                    ),
                },
                {
                    yearLabel: dayjs().format('YYYY'),
                    months: generateFakeMonthsData(
                        1,
                        dayjs().month() + 1,
                        3,
                    ),
                },
            ],
            sequences: FoodSocial.defaultData.mentions.sequences,
        },
        engagement: {
            yearGrowthPct: 10,
            monthGrowthPct: 5,
            data: [
                {
                    yearLabel: dayjs().add(-2, 'year').format('YYYY'),
                    months: generateFakeMonthsData(
                        dayjs().month() + 2,
                        12 - dayjs().month() - 1,
                    ),
                },
                {
                    yearLabel: dayjs().add(-1, 'year').format('YYYY'),
                    months: generateFakeMonthsData(
                        1,
                        12,
                    ),
                },
                {
                    yearLabel: dayjs().format('YYYY'),
                    months: generateFakeMonthsData(
                        1,
                        dayjs().month() + 1,
                    ),
                },
            ],
            sequences: FoodSocial.defaultData.engagement.sequences,
        },
        foodAssociations: [
            { id: '1', name: 'SALSA', count: 100, oneMonthGrowthPct: 10 },
            { id: '2', name: 'GUACAMOLE', count: 90, oneMonthGrowthPct: 5 },
            { id: '3', name: 'MARGARITA', count: 80, oneMonthGrowthPct: 0 },
            { id: '4', name: 'BURRITO', count: 70, oneMonthGrowthPct: -5 },
            { id: '5', name: 'NACHOS', count: 60, oneMonthGrowthPct: -10 },
        ],
        placesAssociations: [
            { id: '1', name: 'SUBWAY', count: 100, oneMonthGrowthPct: 10 },
            { id: '2', name: 'CHIPOTLE', count: 90, oneMonthGrowthPct: 5 },
            { id: '3', name: 'TACO BELL', count: 80, oneMonthGrowthPct: 0 },
            { id: '4', name: 'QDOBA', count: 70, oneMonthGrowthPct: -5 },
            { id: '5', name: 'MOE\'S', count: 60, oneMonthGrowthPct: -10 },
        ],
        posts: SocialPosts.fakeData,
    }

    permissionLevel = PermissionLevelEnum.Full;
    hasData = true;
    data = FoodSocial.defaultData;

    constructor(data?: IData) {
        if (data?.apiModel) {
            this.mapFromApiModel(data.apiModel);
        }
    }

    private setData(data: Partial<IFoodSocial>) {
        Object.assign(this, data);
    }

    private mapFromApiModel(apiModel: ApiModel) {
        const keywordSummary = apiModel.keywordSummary;
        const keywordPosts = apiModel.keywordPosts;
        
        const summaryData = keywordSummary?.data;
        const hasSummaryData = keywordSummary?.hasData ?? false;
        const summaryPermissionLevel = keywordSummary?.permissionLevel;
        const postsData = keywordPosts?.data;

        let data = { ...FoodSocial.fakeData };

        if (summaryData) {
            data = {
                mentions: this.mapMentionsData(summaryData),
                engagement: this.mapEngagementData(summaryData),
                foodAssociations: this.mapAssociations(summaryData.associations?.foods ?? []),
                placesAssociations: this.mapAssociations(summaryData.associations?.places ?? []),
                posts: FoodSocial.fakeData.posts,
            };
        }

        if (postsData) {
            data.posts = new SocialPosts({ apiModel: postsData });
        }

        this.setData({
            permissionLevel: PermissionSection.getPermissionLevel(summaryPermissionLevel),
            hasData: hasSummaryData,
            data,
        });
    }

    private getYears(data: components["schemas"]["SocialKeywordSummaryViewModel"]): number[] {
        return getUnique((data.mentions ?? []).map(i => i.periodYear ?? 0));
    }

    private getMonths(data: components["schemas"]["SocialKeywordSummaryViewModel"], year: number): number[] {
        return getUnique(
            (data.mentions ?? [])
                .filter(i => i.periodYear === year)
                .map(i => i.periodMonth ?? 0)
        );
    }

    private mapMentionsData(data: components["schemas"]["SocialKeywordSummaryViewModel"]): ISocialSlice {
        const mentionsAggregation = (data.aggregations ?? []).find(
            i => includesInLC(i.type ?? '', 'mention')
        );

        const mentionsData: ISocialSlice['data'] = [];

        const years = this.getYears(data);

        years.forEach(year => {
            const months = this.getMonths(data, year);

            months.sort((a, b) => a - b);

            mentionsData.push({
                yearLabel: `${year}`,
                months: months.map(month => {
                    const monthData = (data.mentions ?? [])
                        .find(i => i.periodYear === year && i.periodMonth === month);

                    return {
                        month: month,
                        values: FoodSocial.defaultData.mentions.sequences.map(sequence => {
                            return (monthData?.sentiments ?? []).find(i => includesInLC(i.value ?? '', sequence.title))?.count ?? 0;
                        }),
                    };
                }),
            });
        });

        return {
            yearGrowthPct: mentionsAggregation?.twelveMonthGrowth ?? 0,
            monthGrowthPct: mentionsAggregation?.thirtyDayGrowth ?? 0,
            data: mentionsData,
            sequences: FoodSocial.defaultData.mentions.sequences,
        };
    }

    private mapEngagementData(data: components["schemas"]["SocialKeywordSummaryViewModel"]): ISocialSlice {
        const engagementAggregation = (data.aggregations ?? []).find(
            i => includesInLC(i.type ?? '', 'reach')
        );

        const engagementData: ISocialSlice['data'] = [];

        const years = this.getYears(data);

        years.forEach(year => {
            const months = this.getMonths(data, year);

            months.sort((a, b) => a - b);

            engagementData.push({
                yearLabel: `${year}`,
                months: months.map(month => {
                    const monthData = (data.mentions ?? [])
                        .find(i => i.periodYear === year && i.periodMonth === month);

                    return {
                        month: month,
                        values: [
                            (monthData?.sentiments ?? []).reduce((acc, sentiment) => {
                                return acc + (sentiment.engagementCount ?? 0);
                            }, 0)
                        ],
                    };
                }),
            });
        });

        return {
            yearGrowthPct: engagementAggregation?.twelveMonthGrowth ?? 0,
            monthGrowthPct: engagementAggregation?.thirtyDayGrowth ?? 0,
            data: engagementData,
            sequences: FoodSocial.defaultData.engagement.sequences,
        };
    }

    private mapAssociations(data: components["schemas"]["SocialKeywordAssociationItemViewModel"][]): IAssociation[] {
        return data.map((item, index) => ({
            id: index,
            name: item.value ?? '',
            count: item.count ?? 0,
            oneMonthGrowthPct: item.percentageGrowth ?? 0,
        }));
    }
}